import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';

// Enter the fullscreen icon
const FULLSCREEN_ENTER_SVG =
	'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
	'<path fill="currentColor" d="M11.5 5.75a.75.75 0 0 1 0-1.5H15a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0V6.81l-2.72 2.72a.75.75 0 0 1-1.06-1.06l2.72-2.72z"/>' +
	'<path fill="currentColor" d="M9.53 10.47a.75.75 0 0 1 0 1.06l-2.72 2.72H8.5a.75.75 0 0 1 0 1.5H5a.75.75 0 0 1-.75-.75v-3.5a.75.75 0 0 1 1.5 0v1.69l2.72-2.72a.75.75 0 0 1 1.06 0z"/>' +
	'<path fill="currentColor" d="M2 0h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2m16 1.5H2a.5.5 0 0 0-.5.5v16a.5.5 0 0 0 .5.5h16a.5.5 0 0 0 .5-.5V2a.5.5 0 0 0-.5-.5z"/>' +
	'</svg>';

// Exit the fullscreen icon
const FULLSCREEN_EXIT_SVG =
	'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
	'<path fill="currentColor" d="M15.53 5.53a.75.75 0 0 0-1.06-1.06l-2.72 2.72V5.5a.75.75 0 0 0-1.5 0V9a.75.75 0 0 0 .75.75h3.5a.75.75 0 0 0 0-1.5h-1.69z"/>' +
	'<path fill="currentColor" d="M5.5 10.25a.75.75 0 0 0 0 1.5h1.69l-2.72 2.72a.75.75 0 0 0 1.06 1.06l2.72-2.72v1.69a.75.75 0 0 0 1.5 0V11a.75.75 0 0 0-.75-.75z"/>' +
	'<path fill="currentColor" d="M0 2a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm18.5 0a.5.5 0 0 0-.5-.5H2a.5.5 0 0 0-.5.5v16a.5.5 0 0 0 .5.5h16a.5.5 0 0 0 .5-.5z"/>' +
	'</svg>';

export default class FullscreenTogglePlugin extends Plugin {
	static get pluginName() {
		return 'FullscreenTogglePlugin';
	}

	// Prevent prod config stripping
	static get config() {
		return {
			fullscreen: {
				callbacks: {
					onEnter: null,
					onExit: null
				}
			}
		};
	}

	init() {
		const editor = this.editor;
		const doc = editor.editing.view.document.domRoot ?
			editor.editing.view.document.domRoot.ownerDocument :
			// eslint-disable-next-line no-undef
			window.document;

		let isOn = false;
		let buttonView = null;

		function getRoot() {
			if (
				editor &&
				editor.ui &&
				editor.ui.view &&
				editor.ui.view.element
			) {
				return editor.ui.view.element;
			}
			return null;
		}

		function getCallbacks() {
			const cfg = editor.config.get( 'fullscreen' );
			return ( cfg && cfg.callbacks ) ? cfg.callbacks : {};
		}

		function addFullscreen( source ) {
			if ( isOn ) {
				return;
			}
			source = source || 'button';

			const el = getRoot();
			if ( !el ) {
				return;
			}

			el.classList.add( 'ck-editor--fullscreen' );
			el.style.width = '100vw';
			el.style.height = '100vh';

			doc.body.classList.add( 'ck-editor-body--no-scroll' );

			isOn = true;
			updateButton();

			const onEnter = getCallbacks().onEnter;
			if ( typeof onEnter === 'function' ) {
				onEnter( { editor, source } );
			}
		}

		function removeFullscreen( source ) {
			if ( !isOn ) {
				return;
			}
			source = source || 'esc';

			const el = getRoot();
			if ( !el ) {
				return;
			}

			el.classList.remove( 'ck-editor--fullscreen' );
			el.style.width = '';
			el.style.height = '';

			doc.body.classList.remove( 'ck-editor-body--no-scroll' );

			isOn = false;
			updateButton();

			const onExit = getCallbacks().onExit;
			if ( typeof onExit === 'function' ) {
				onExit( { editor, source } );
			}
		}

		function toggleFullscreen( source ) {
			if ( isOn ) {
				removeFullscreen( source );
			} else {
				addFullscreen( source );
			}
		}

		function updateButton() {
			if ( !buttonView ) {
				return;
			}

			buttonView.set( {
				icon: isOn ? FULLSCREEN_EXIT_SVG : FULLSCREEN_ENTER_SVG,
				label: isOn ? 'Exit Fullscreen (Esc)' : 'Fullscreen (Ctrl+Shift+F)',
				tooltip: true,
				isOn
			} );
		}

		editor.ui.componentFactory.add( 'fullscreen', function( locale ) {
			buttonView = new ButtonView( locale );

			buttonView.set( {
				icon: FULLSCREEN_ENTER_SVG,
				label: 'Fullscreen (Ctrl+Shift+F)',
				tooltip: true,
				withText: false,
				isToggleable: true,
				isOn
			} );

			buttonView.on( 'execute', function() {
				toggleFullscreen( 'button' );
			} );

			editor.keystrokes.set( 'Ctrl+Shift+F', function( data, cancel ) {
				toggleFullscreen( 'shortcut' );
				cancel();
			} );

			editor.keystrokes.set( 'Esc', function( data, cancel ) {
				if ( isOn ) {
					removeFullscreen( 'esc' );
					cancel();
				}
			} );

			return buttonView;
		} );

		function onDocEsc( e ) {
			if ( !isOn ) {
				return;
			}
			if ( e.key === 'Escape' || e.keyCode === 27 ) {
				removeFullscreen( 'esc' );
			}
		}

		doc.addEventListener( 'keydown', onDocEsc );

		this.listenTo( editor, 'destroy', function() {
			doc.removeEventListener( 'keydown', onDocEsc );
			removeFullscreen( 'destroy' );
		} );
	}
}
